<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import type { GameConfig, PhaseLengthsRange, Range } from '../../../../shared/api/game.ts';
  import { userAction } from '../../../../shared/observability/userAction.ts';
  import { Stepper } from '../../../../shared/ui/stepper';
  import type { Setting, SettingsChange } from '../../model/settingsSaving.ts';

  const phaseLengthStepSeconds = 5;

  const props = defineProps<{
    config: GameConfig;
    roundsRange: Range;
    phaseLengthsRange: PhaseLengthsRange;
    failures: Partial<Record<Setting, string>>;
  }>();
  const emit = defineEmits<{ change: [change: SettingsChange] }>();

  const unsaved = ref<Partial<Record<Setting, number>>>({});

  const shown = (setting: Setting) => unsaved.value[setting] ?? props.config[setting];

  watch(
    () => props.config,
    (saved) => {
      for (const setting of Object.keys(unsaved.value) as Setting[]) {
        if (saved[setting] === unsaved.value[setting]) delete unsaved.value[setting];
      }
    },
  );

  watch(
    () => props.failures,
    (failed) => {
      for (const setting of Object.keys(failed) as Setting[]) delete unsaved.value[setting];
    },
  );

  const change = (setting: Setting, value: number) => {
    unsaved.value[setting] = value;
    emit('change', { [setting]: value });
  };
</script>

<template>
  <div class="game-settings">
    <Stepper
      :action-name="userAction.lobbyChangeRounds"
      :error="failures.rounds"
      :max="roundsRange.most"
      :min="roundsRange.fewest"
      :model-value="shown('rounds')"
      decrease-label="Fewer rounds"
      increase-label="More rounds"
      label="Rounds"
      @update:model-value="change('rounds', $event)"
    />
    <Stepper
      :action-name="userAction.lobbyChangePhaseLength"
      :error="failures.chooserTime"
      :max="phaseLengthsRange.chooserTime.most"
      :min="phaseLengthsRange.chooserTime.fewest"
      :model-value="shown('chooserTime')"
      :step="phaseLengthStepSeconds"
      decrease-label="Shorter choosing"
      increase-label="Longer choosing"
      label="Choosing"
      unit=" s"
      @update:model-value="change('chooserTime', $event)"
    />
    <Stepper
      :action-name="userAction.lobbyChangePhaseLength"
      :error="failures.guessersTime"
      :max="phaseLengthsRange.guessersTime.most"
      :min="phaseLengthsRange.guessersTime.fewest"
      :model-value="shown('guessersTime')"
      :step="phaseLengthStepSeconds"
      decrease-label="Shorter guessing"
      increase-label="Longer guessing"
      label="Guessing"
      unit=" s"
      @update:model-value="change('guessersTime', $event)"
    />
  </div>
</template>

<style scoped>
  .game-settings {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    width: 100%;
  }
</style>
